import { useContext } from 'react';
import strings from '../../../../strings/strings.json';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import { ActivityType } from 'models/Activity';
import { CourseNavigation } from './CourseNavigation';

type CourseNavigationActivityProps = {
    questionnaireActive?: boolean;
};

export function CourseNavigationActivity({ questionnaireActive }: CourseNavigationActivityProps): JSX.Element {
    const learnerCourse = useContext(LearnerCourseContext).learnerCourse!;
    const { step, setStep } = useContext(LearnerCourseContext);

    let previousButtonTitle = '';
    let previousButtonAria = '';
    let previousButtonAction = () => {};
    let nextButtonTitle = '';
    let nextButtonAria = '';
    let nextButtonAction = () => {};
    let showSkipButton = false;

    const currentActivity = learnerCourse.modules
        .flatMap((module) => module.activities)
        .find((activity) => activity.activityStatusId === step.id)!;

    const module = learnerCourse.modules.find((module) => module.id === currentActivity.moduleId)!;
    const moduleActivities = module.activities.filter((a) => a.type !== ActivityType.PRESENTATION_SLIDE);

    const currentActivityIndex = moduleActivities.findIndex((activity) => activity.activityStatusId === step.id);

    const firstActivityInModule = currentActivityIndex === 0;

    if (firstActivityInModule) {
        previousButtonTitle = module.title;
        previousButtonAria = `${strings.immersiveCourseViewButtons.backButton.aria} ${module.title}`;
        previousButtonAction = () => setStep({ id: module.id, stepType: 'Module' });
    } else {
        const previousActivity = moduleActivities[currentActivityIndex - 1];
        previousButtonTitle = previousActivity.title;
        previousButtonAria = `${strings.immersiveCourseViewButtons.backButton.aria} ${previousActivity.title}`;
        previousButtonAction = () => setStep({ id: previousActivity.activityStatusId, stepType: 'Activity' });
    }

    const lastActivityInModule = currentActivityIndex === moduleActivities.length - 1;

    if (lastActivityInModule) {
        const moduleIndex = learnerCourse.modules.findIndex((m) => m.id === module.id);
        const isLastModuleInCourse = moduleIndex === learnerCourse.modules.length - 1;

        if (isLastModuleInCourse) {
            nextButtonTitle = learnerCourse.title;
            nextButtonAria = `${strings.immersiveCourseViewButtons.nextButton.aria} ${learnerCourse.title}`;
            nextButtonAction = () => setStep({ id: '', stepType: 'Course' });
        } else {
            const nextModule = learnerCourse.modules[moduleIndex + 1];
            nextButtonTitle = nextModule.title;
            nextButtonAria = `${strings.immersiveCourseViewButtons.nextButton.aria} ${nextModule.title}`;
            nextButtonAction = () => setStep({ id: nextModule.id, stepType: 'Module' });
        }
    } else {
        const nextActivity = moduleActivities[currentActivityIndex + 1];
        nextButtonTitle = nextActivity.title;
        nextButtonAria = `${strings.immersiveCourseViewButtons.nextButton.aria} ${nextActivity.title}`;
        nextButtonAction = () => setStep({ id: nextActivity.activityStatusId, stepType: 'Activity' });
    }

    if (questionnaireActive) {
        nextButtonTitle = strings.questionnairePage.skipQuestionnaireButtonTitle;
        showSkipButton = true;
    }

    return (
        <CourseNavigation
            showSkipButton={showSkipButton}
            previousButtonTitle={previousButtonTitle}
            previousButtonAria={previousButtonAria}
            previousButtonAction={previousButtonAction}
            nextButtonTitle={nextButtonTitle}
            nextButtonAria={nextButtonAria}
            nextButtonAction={nextButtonAction}
        />
    );
}
